import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import Choices, { Options } from "choices.js";
import "choices.js/public/assets/styles/choices.css";

export default class extends Controller {
  // MEMO: dateを渡した場合クエリパラメータが付与されるので、separatorにクエリパラメータが含まれる場合はdateは使えません
  static values = { url: String, separator: String, date: String };

  declare readonly urlValue: string;
  declare readonly separatorValue: string;
  declare readonly dateValue: string;

  private choices: Choices | null = null;

  connect() {
    if (this.choices) return; // Choices.js が select をラップする際のDOM移動でconnectが再発火するのを無視する

    this.choices = new Choices(this.element, {
      allowHTML: false,
      editItems: true,
      removeItemButton: false,
      shouldSort: false,
      searchFields: ["label"],
      // Choices.jsのデフォルトは検索結果が4件に制限されるため、無制限にする（-1）
      searchResultLimit: -1,
      noResultsText: "選択肢がありません", // 検索結果がないときに表示するメッセージ
      noChoicesText: "該当する結果がありません", // 選択可能なオプションがないときに表示するメッセージ
      itemSelectText: "クリックして選択してください",
      classNames: {
        containerOuter: "choices",
        containerInner: [
          "block",
          "w-full",
          "rounded-md",
          "border",
          "border-gray-300",
          "bg-white",
          "px-2.5",
          "py-1.5",
          "text-gray-900",
          "focus:border-blue-500",
          "focus:ring-blue-500",
        ],
        input: "choices__input",
        inputCloned: "choices__input--cloned",
        list: "choices__list",
        listItems: "choices__list--multiple",
        listSingle: "choices__list--single",
        listDropdown: "choices__list--dropdown",
        item: "choices__item",
        itemSelectable: "choices__item--selectable",
        itemDisabled: "choices__item--disabled",
        itemChoice: "choices__item--choice",
        placeholder: "choices__placeholder",
        group: "choices__group",
        groupHeading: "choices__heading",
        button: "choices__button",
        activeState: "is-active",
        focusState: "is-focused",
        openState: "is-open",
        disabledState: "is-disabled",
        highlightedState: "is-highlighted",
        selectedState: "is-selected",
        flippedState: "is-flipped",
        loadingState: "is-loading",
        noResults: "has-no-results",
        noChoices: "has-no-choices",
      },
    } as Partial<Options>);
  }

  disconnect() {
    // Choices.js のラップ移動では要素はdocument内に残るため、実際に取り除かれた時のみ破棄する
    if (this.element.isConnected) return;

    this.choices?.destroy();
    this.choices = null;
  }

  change(event: Event) {
    const target = event.target as HTMLSelectElement;
    let url = this.urlValue;
    if (target.value !== "") {
      url += `${this.separatorValue}${target.value}`;
    }
    if (this.dateValue !== "") {
      url += `?date=${this.dateValue}`;
    }
    this.safeNavigate(url);
  }

  navigate(event: Event) {
    const url = (event.target as HTMLSelectElement).value || this.urlValue;
    this.safeNavigate(url);
  }

  private safeNavigate(url: string) {
    if (!url.startsWith("/")) return;
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) {
      Turbo.visit(parsed.pathname + parsed.search);
    }
  }
}
